import { canDrawBattleRive, hasBattleRive } from './battleRive';
import type { SlotBattle } from './types';

export type BattlePane = 'opening' | 'standings' | 'leaderboard' | 'counting' | 'closing';

/**
 * Which panes the single lobby dialog carries, in swipe order. The ticket's "pages" are swipes, not
 * routes — one mount, no history entries — so this is the whole variant router. `countingResults` is
 * the store's `isCountingResults`: the battle has ended, by the client's clock, with no closing yet.
 *
 * Closing is exclusive and non-dismissible: once a segment has settled there is one thing left to
 * do — claim the reward, which is forfeited if left past the claim window — and swiping back to a
 * frozen graph is not it.
 */
export function selectBattlePanes(
  battle: SlotBattle | null,
  openingSeen = false,
  riveFailed = false,
  countingResults = false,
): BattlePane[] {
  if (!battle) {
    return [];
  }

  if (battle.closing) {
    // NEXT TIME exists only as the Rive screen (design): with no file, no winner to draw, or a file that
    // failed or lacks the screen, a player the battle paid nothing gets no popup at all, never a text
    // pane. The store acknowledges that closing quietly instead (`acknowledgeHiddenClosing`).
    if (battle.closing.outcome === 'lost_no_reward' && (riveFailed || !canDrawBattleRive(battle, 'next_time'))) {
      return [];
    }

    return ['closing'];
  }

  // Settled with no closing payload means the 7-day claim window has passed. Nothing renders — the
  // read is expected to 204 by then, and this is the client half of the same rule.
  if (battle.state === 'settled') {
    return [];
  }

  // Between the end and the closing the standings are frozen and about to be replaced by the result:
  // say the results are being counted instead. `countingResults` is the end as the client's clock sees
  // it, for a server still answering `live` for its grace after the end.
  if (battle.state === 'settling' || countingResults) {
    return ['counting'];
  }

  // Podium, then leaderboard — always both. Before the first bet lands there is no leading slot, so
  // the leaderboard pane shows only the player's own progress and an empty list.
  const panes: BattlePane[] = ['standings', 'leaderboard'];

  // Show-once intro, drawn by the run's Rive file. Receiving the event trigger IS the opt-in, so its
  // CTA only dismisses. With no .riv, or one that failed to load this session, it is skipped.
  if (battle.opening?.required && hasBattleRive(battle) && !openingSeen && !riveFailed) {
    panes.unshift('opening');
  }

  return panes;
}

/** The closing pane is the only non-dismissible one (design: three states, single CTA). */
export function isBattleDialogDismissible(panes: BattlePane[]): boolean {
  return panes[0] !== 'closing';
}
